import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CopyIcon, EyeIcon, EyeOffIcon, GlobeIcon, WifiIcon, CreditCardIcon, SmartphoneIcon, ShieldAlertIcon, Edit2Icon } from 'lucide-react';
import { TopBar } from '../components/TopBar';
interface CardSettingsProps {
  navigate: (screen: string) => void;
}
export function CardSettings({
  navigate
}: CardSettingsProps) {
  const [showCvv, setShowCvv] = useState(false);
  // Toggles state
  const [toggles, setToggles] = useState({
    online: true,
    atm: true,
    pos: true,
    contactless: false,
    international: false
  });
  const handleToggle = (key: keyof typeof toggles) => {
    setToggles(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  const ToggleSwitch = ({
    active
  }: {
    active: boolean;
  }) => <div className={`w-11 h-6 rounded-full relative shadow-inner transition-colors ${active ? 'bg-emerald-500' : 'bg-gray-300'}`} data-id="element-276">
      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm transition-all ${active ? 'right-1' : 'left-1'}`} data-id="element-277"></div>
    </div>;
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="flex-1 overflow-y-auto pb-safe bg-slate-50" data-id="element-278">
      <TopBar title="Card Settings" showBack onBack={() => navigate('wallet')} data-id="element-279" />

      <div className="px-5 pt-4 pb-8" data-id="element-280">
        {/* Mini Card Preview */}
        <div className="relative perspective-1000 mb-8 max-w-[280px] mx-auto" data-id="element-281">
          <div className="relative rounded-2xl p-5 h-40 text-white shadow-xl bg-gradient-to-br from-[#0F2B27] via-[#1B3B36] to-[#2A5A4A] overflow-hidden" data-id="element-282">
            <div className="absolute top-2 right-2 w-24 h-24 rounded-full border-[8px] border-amber-500/10" data-id="element-283"></div>
            <div className="absolute top-8 right-12 w-16 h-16 rounded-full border-[6px] border-emerald-500/10" data-id="element-284"></div>

            <div className="relative z-10 h-full flex flex-col justify-between" data-id="element-285">
              <div className="flex justify-between items-start" data-id="element-286">
                <div className="font-bold text-sm tracking-[0.2em] text-white/90" data-id="element-287">
                  SKYDD
                </div>
                <div className="w-8 h-6 bg-gradient-to-br from-amber-200 to-amber-400 rounded-md shadow-sm opacity-90" data-id="element-288"></div>
              </div>
              <div data-id="element-289">
                <div className="text-lg tracking-[0.15em] font-mono mb-2 text-white/90" data-id="element-290">
                  **** **** **** 4092
                </div>
                <div className="flex justify-between items-center text-xs text-white/80 font-medium" data-id="element-291">
                  <span className="uppercase tracking-widest text-[10px]" data-id="element-292">
                    Adaeze Okonkwo
                  </span>
                  <span className="font-mono tracking-wider" data-id="element-293">12/28</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Card Details */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100 mb-6" data-id="element-294">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-295">
            Card Details
          </h3>

          <div className="space-y-4" data-id="element-296">
            <div className="flex justify-between items-center pb-4 border-b border-gray-50" data-id="element-297">
              <div data-id="element-298">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-299">
                  Card Number
                </p>
                <p className="font-mono font-bold text-[#1B3B36] tracking-wider" data-id="element-300">
                  4092 1234 5678 9012
                </p>
              </div>
              <button className="p-2 bg-gray-50 rounded-lg text-gray-500 hover:text-[#1B3B36] transition-colors" data-id="element-301">
                <CopyIcon className="w-4 h-4" data-id="element-302" />
              </button>
            </div>

            <div className="flex gap-6" data-id="element-303">
              <div className="flex-1" data-id="element-304">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-305">
                  Expiry Date
                </p>
                <p className="font-mono font-bold text-[#1B3B36]" data-id="element-306">12/28</p>
              </div>
              <div className="flex-1" data-id="element-307">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-308">
                  CVV
                </p>
                <div className="flex items-center gap-3" data-id="element-309">
                  <p className="font-mono font-bold text-[#1B3B36]" data-id="element-310">
                    {showCvv ? '482' : '***'}
                  </p>
                  <button onClick={() => setShowCvv(!showCvv)} className="text-gray-400 hover:text-[#1B3B36]" data-id="element-311">
                    {showCvv ? <EyeOffIcon className="w-4 h-4" data-id="element-312" /> : <EyeIcon className="w-4 h-4" data-id="element-313" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* PIN Management */}
        <div className="bg-white rounded-3xl p-2 shadow-sm border border-gray-100 mb-6 flex" data-id="element-314">
          <button className="flex-1 py-3 text-sm font-bold text-[#1B3B36] hover:bg-gray-50 rounded-2xl transition-colors" data-id="element-315">
            View PIN
          </button>
          <div className="w-px bg-gray-100 my-2" data-id="element-316"></div>
          <button className="flex-1 py-3 text-sm font-bold text-[#1B3B36] hover:bg-gray-50 rounded-2xl transition-colors" data-id="element-317">
            Change PIN
          </button>
        </div>

        {/* Transaction Controls */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100 mb-6" data-id="element-318">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-319">
            Transaction Controls
          </h3>

          <div className="space-y-1" data-id="element-320">
            {[{
            id: 'online',
            icon: SmartphoneIcon,
            label: 'Online Transactions',
            desc: 'Websites and apps'
          }, {
            id: 'atm',
            icon: CreditCardIcon,
            label: 'ATM Withdrawals',
            desc: 'Cash from ATMs'
          }, {
            id: 'pos',
            icon: CreditCardIcon,
            label: 'POS Payments',
            desc: 'In-store terminals'
          }, {
            id: 'contactless',
            icon: WifiIcon,
            label: 'Contactless',
            desc: 'Tap to pay'
          }, {
            id: 'international',
            icon: GlobeIcon,
            label: 'International',
            desc: 'Outside Nigeria'
          }].map((item, idx, arr) => <div key={item.id} className={`flex items-center justify-between py-3 ${idx !== arr.length - 1 ? 'border-b border-gray-50' : ''}`} data-id="element-321">
                <div className="flex items-center gap-3" data-id="element-322">
                  <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-500" data-id="element-323">
                    <item.icon className="w-4 h-4" data-id="element-324" />
                  </div>
                  <div data-id="element-325">
                    <p className="text-sm font-bold text-[#1B3B36]" data-id="element-326">
                      {item.label}
                    </p>
                    <p className="text-[10px] font-medium text-gray-500" data-id="element-327">
                      {item.desc}
                    </p>
                  </div>
                </div>
                <button onClick={() => handleToggle(item.id as keyof typeof toggles)} data-id="element-328">
                  <ToggleSwitch active={toggles[item.id as keyof typeof toggles]} data-id="element-329" />
                </button>
              </div>)}
          </div>
        </div>

        {/* Spending Limits */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100 mb-6" data-id="element-330">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-331">
            Spending Limits
          </h3>

          <div className="space-y-1" data-id="element-332">
            {[{
            label: 'Daily Limit',
            value: '₦500,000'
          }, {
            label: 'Monthly Limit',
            value: '₦5,000,000'
          }, {
            label: 'ATM Daily',
            value: '₦200,000'
          }].map((item, idx, arr) => <div key={idx} className={`flex items-center justify-between py-3 ${idx !== arr.length - 1 ? 'border-b border-gray-50' : ''}`} data-id="element-333">
                <div data-id="element-334">
                  <p className="text-sm font-bold text-[#1B3B36]" data-id="element-335">
                    {item.label}
                  </p>
                  <p className="text-xs font-medium text-gray-500" data-id="element-336">
                    {item.value}
                  </p>
                </div>
                <button className="p-2 text-gray-400 hover:text-amber-500 transition-colors" data-id="element-337">
                  <Edit2Icon className="w-4 h-4" data-id="element-338" />
                </button>
              </div>)}
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-rose-100" data-id="element-339">
          <h3 className="text-xs font-bold text-rose-400 uppercase tracking-wider mb-4" data-id="element-340">
            Danger Zone
          </h3>

          <button className="w-full py-3.5 rounded-xl border-2 border-rose-500 text-rose-500 font-bold text-sm mb-3 hover:bg-rose-50 transition-colors active:scale-95 flex items-center justify-center gap-2" data-id="element-341">
            <ShieldAlertIcon className="w-4 h-4" data-id="element-342" /> Block & Replace Card
          </button>

          <div className="text-center" data-id="element-343">
            <button className="text-xs font-bold text-gray-500 hover:text-rose-500 underline decoration-gray-300 underline-offset-4" data-id="element-344">
              Report Lost or Stolen
            </button>
          </div>
        </div>
      </div>
    </motion.div>;
}